import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { EventType } from '../apollo/__generated__/graphql';
import { useCarpoolTripsQuery } from '../carpool/hooks/useCarpoolTripsQuery';
import { HeaderCard } from '../event/components/HeaderCard';
import { useCurrentEventQuery } from '../event/hooks/useCurrentEventQuery';
import { Layout } from '../navigation/components/Layout/Layout';
import { useCurrentUserQuery } from '../user/hooks/useCurrentUserQuery';

type DashboardTaskStatus = 'todo' | 'blocked' | 'pending' | 'done';

interface DashboardTask {
  id: string;
  title: string;
  description: string;
  destination?: string;
  isOptional?: boolean;
  status: DashboardTaskStatus;
}

const statusLabelByTaskStatus: Record<DashboardTaskStatus, string> = {
  todo: 'À faire',
  blocked: 'Bloqué',
  pending: 'En attente',
  done: 'Terminé',
};

const statusColorByTaskStatus: Record<
  DashboardTaskStatus,
  'primary' | 'default' | 'info' | 'success'
> = {
  todo: 'primary',
  blocked: 'default',
  pending: 'info',
  done: 'success',
};

interface DashboardTaskRowProps {
  onNavigate: (destination: string) => void;
  task: DashboardTask;
}

const DashboardTaskRow = ({ onNavigate, task }: DashboardTaskRowProps) => {
  const isPressable = Boolean(task.destination);

  return (
    <Box
      onClick={() => {
        if (task.destination) {
          onNavigate(task.destination);
        }
      }}
      onKeyDown={(event) => {
        if (!task.destination) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onNavigate(task.destination);
        }
      }}
      role={isPressable ? 'button' : undefined}
      tabIndex={isPressable ? 0 : undefined}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        cursor: isPressable ? 'pointer' : 'default',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
        '&:hover': isPressable
          ? {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            }
          : undefined,
        '&:focus-visible': isPressable
          ? {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            }
          : undefined,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {task.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {task.description}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          width={{ xs: '100%', sm: 'auto' }}
          justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          {task.destination ? (
            <Button
              variant={task.isOptional ? 'outlined' : 'contained'}
              onClick={() => {
                if (task.destination) {
                  onNavigate(task.destination);
                }
              }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Y aller
            </Button>
          ) : null}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            alignItems="center"
          >
            {task.isOptional ? (
              <Chip
                label="Optionnel"
                variant="outlined"
                sx={{
                  alignSelf: 'center',
                  color: 'primary.main',
                  borderColor: 'primary.main',
                }}
              />
            ) : null}

            <Chip
              label={statusLabelByTaskStatus[task.status]}
              color={statusColorByTaskStatus[task.status]}
              variant={
                task.status === 'blocked' || task.status === 'pending'
                  ? 'outlined'
                  : 'filled'
              }
              sx={{ alignSelf: 'center' }}
            />
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const { data: currentUserData } = useCurrentUserQuery();
  const { data: currentEventData } = useCurrentEventQuery();
  const { data: carpoolTripsData } = useCarpoolTripsQuery(
    currentEventData?.currentEvent?.id ?? null,
  );

  const currentEvent = currentEventData?.currentEvent ?? null;
  const currentUser = currentUserData?.currentUser ?? null;

  const tasks = useMemo(() => {
    if (!currentEvent || !currentUser) {
      return [];
    }

    const hasPersonalInformation = currentUser.firstname !== null;
    const hasImages = (currentUser.images ?? []).length > 0;
    const isCurrentEventBedroomAndTent =
      currentEvent.type === EventType.BedroomAndTent;
    const isCurrentEventBedroomOnly =
      currentEvent.type === EventType.BedroomOnly;
    const hasOrganizedCarpool = (carpoolTripsData?.carpoolTrips ?? []).some(
      (trip) =>
        trip.driver.id === currentUser.id ||
        trip.passengers.some((passenger) => passenger.id === currentUser.id),
    );

    const dashboardTasks: DashboardTask[] = [
      {
        id: 'personal-information',
        title: 'Compléter vos informations personnelles',
        description: hasPersonalInformation
          ? 'Vos informations sont complètes.'
          : 'Ajoutez vos informations pour finaliser votre inscription.',
        destination: '/personal-information',
        status: hasPersonalInformation ? 'done' : 'todo',
      },
      {
        id: 'join-event',
        title: `S'inscrire au ${currentEvent.name}`,
        description: currentEvent.hasJoined
          ? 'Vous êtes bien inscrit à cet événement.'
          : hasPersonalInformation
            ? "Votre inscription n'est pas encore finalisée."
            : 'Disponible après avoir complété vos informations personnelles.',
        destination: '/event-register',
        status: currentEvent.hasJoined
          ? 'done'
          : hasPersonalInformation
            ? 'todo'
            : 'blocked',
      },
      {
        id: 'pay',
        title: 'Payer votre participation',
        description: currentEvent.hasPaidTicket
          ? 'Votre place est payée.'
          : currentEvent.hasJoined
            ? 'Le paiement de votre place est attendu.'
            : "Disponible après votre inscription à l'événement.",
        destination: '/payment',
        status: currentEvent.hasPaidTicket
          ? 'done'
          : currentEvent.hasJoined
            ? 'todo'
            : 'blocked',
      },
      {
        id: 'images',
        title: 'Ajouter vos photos',
        description: hasImages
          ? 'Vos photos sont bien enregistrées.'
          : 'Optionnel. Ajoutez des photos pour compléter votre profil.',
        destination: '/images',
        isOptional: true,
        status: hasImages ? 'done' : 'todo',
      },
      {
        id: 'caution',
        title: 'Envoyer vos chèques de caution',
        description: currentEvent.hasCaution
          ? 'Votre caution a bien été prise en compte.'
          : currentEvent.hasJoined
            ? "L'orga attend encore votre caution."
            : "Disponible après votre inscription à l'événement.",
        destination: '/caution',
        status: currentEvent.hasCaution
          ? 'done'
          : currentEvent.hasJoined
            ? 'todo'
            : 'blocked',
      },
    ];

    if (isCurrentEventBedroomAndTent) {
      dashboardTasks.push({
        id: 'pay-bedroom',
        title: 'Payer une place en chambre',
        description: currentEvent.hasPaidBedroom
          ? 'Votre place en chambre est payée.'
          : currentEvent.hasPaidTicket
            ? 'Optionnel. Vous pouvez réserver une place en chambre dès maintenant.'
            : 'Disponible après le paiement de votre place.',
        destination: '/bedrooms',
        isOptional: true,
        status: currentEvent.hasPaidBedroom
          ? 'done'
          : currentEvent.hasPaidTicket
            ? 'todo'
            : 'blocked',
      });
    }

    if (isCurrentEventBedroomAndTent || isCurrentEventBedroomOnly) {
      const canChooseBedroom = isCurrentEventBedroomAndTent
        ? currentEvent.hasPaidBedroom
        : currentEvent.hasPaidTicket;

      dashboardTasks.push({
        id: 'bedroom',
        title: 'Choisir votre chambre',
        description: currentEvent.bedroom
          ? 'Votre chambre est déjà choisie.'
          : canChooseBedroom
            ? 'Sélectionnez votre place en chambre.'
            : isCurrentEventBedroomAndTent
              ? "Disponible après l'achat d'une place en chambre."
              : 'Disponible après le paiement de votre place.',
        destination: '/bedrooms',
        status: currentEvent.bedroom
          ? 'done'
          : canChooseBedroom
            ? 'todo'
            : 'blocked',
      });
    }

    dashboardTasks.push({
      id: 'carpool',
      title: 'Organiser votre covoiturage',
      description: hasOrganizedCarpool
        ? 'Votre trajet est déjà organisé.'
        : currentEvent.hasPaidTicket
          ? 'Optionnel. Proposez un trajet ou rejoignez une voiture.'
          : 'Disponible après le paiement de votre place.',
      destination: '/covoiturage',
      isOptional: true,
      status: hasOrganizedCarpool
        ? 'done'
        : currentEvent.hasPaidTicket
          ? 'todo'
          : 'blocked',
    });

    dashboardTasks.push({
      id: 'tinder',
      title: "S'inscrire au jeu Meetic",
      description: currentEvent.hasJoinedTinder
        ? 'Vous êtes inscrit au jeu Meetic pour cet événement.'
        : currentEvent.isValidated
          ? 'Optionnel. Inscrivez-vous au jeu Meetic pour participer quand il sera ouvert.'
          : "Disponible après validation par l'orga.",
      destination: '/tinder',
      isOptional: true,
      status: currentEvent.hasJoinedTinder
        ? 'done'
        : currentEvent.isValidated
          ? 'todo'
          : 'blocked',
    });

    dashboardTasks.push({
      id: 'validated',
      title: "Validation par l'orga",
      description: currentEvent.isValidated
        ? "Votre participation a été confirmée par l'orga."
        : "Cette étape dépend de l'orga. La validation est faite uniquement pour les profils complets, avec paiement et chèque de caution reçus.",
      status: currentEvent.isValidated ? 'done' : 'pending',
    });

    return dashboardTasks.map((task) =>
      task.status === 'todo' ? task : { ...task, destination: undefined },
    );
  }, [carpoolTripsData?.carpoolTrips, currentEvent, currentUser]);

  const nextTask = useMemo(
    () =>
      tasks.find((task) => task.status === 'todo' && !task.isOptional) ??
      tasks.find((task) => task.status === 'todo' && task.isOptional) ??
      null,
    [tasks],
  );

  const todoTasks = useMemo(
    () => tasks.filter((task) => task.status === 'todo'),
    [tasks],
  );
  const blockedTasks = useMemo(
    () => tasks.filter((task) => task.status === 'blocked'),
    [tasks],
  );
  const pendingTasks = useMemo(
    () => tasks.filter((task) => task.status === 'pending'),
    [tasks],
  );
  const doneTasks = useMemo(
    () => tasks.filter((task) => task.status === 'done'),
    [tasks],
  );
  const progressTotal = useMemo(
    () =>
      tasks.filter((task) => !task.isOptional || task.status === 'done').length,
    [tasks],
  );

  const progress = useMemo(() => {
    if (progressTotal === 0) {
      return 0;
    }

    return Math.round((doneTasks.length / progressTotal) * 100);
  }, [doneTasks.length, progressTotal]);

  if (!currentEvent || !currentUser) {
    return (
      <Layout title="Mon espace" activeTab="dashboard">
        <HeaderCard />
      </Layout>
    );
  }

  return (
    <Layout title="Mon espace" activeTab="dashboard">
      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5">{currentEvent.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(currentEvent.startAt, 'dd/MM/yy')} -{' '}
                  {format(currentEvent.endAt, 'dd/MM/yy')} (Dans{' '}
                  {formatDistanceToNow(currentEvent.startAt, { locale: fr })})
                </Typography>
              </Box>
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Avancement
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {doneTasks.length}/{progressTotal} démarches terminées
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 8, borderRadius: 999 }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">Action recommandée</Typography>
                <Typography variant="body2" color="text.secondary">
                  {nextTask
                    ? 'Voici la prochaine action utile à faire.'
                    : currentEvent.isValidated
                      ? 'Tout est prêt pour votre événement.'
                      : "Vous n'avez plus rien à faire pour le moment."}
                </Typography>
              </Box>
              {nextTask ? (
                <Box
                  onClick={() => {
                    if (nextTask.destination) {
                      navigate(nextTask.destination);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (!nextTask.destination) {
                      return;
                    }

                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(nextTask.destination);
                    }
                  }}
                  role={nextTask.destination ? 'button' : undefined}
                  tabIndex={nextTask.destination ? 0 : undefined}
                  sx={{
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    p: 2,
                    cursor: nextTask.destination ? 'pointer' : 'default',
                    transition:
                      'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                    '&:hover': nextTask.destination
                      ? {
                          transform: 'translateY(-1px)',
                          boxShadow: 4,
                        }
                      : undefined,
                    '&:focus-visible': nextTask.destination
                      ? {
                          outline: '2px solid',
                          outlineColor: 'primary.contrastText',
                          outlineOffset: 2,
                        }
                      : undefined,
                  }}
                >
                  <Stack
                    spacing={2}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {nextTask.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'inherit', opacity: 0.9 }}
                      >
                        {nextTask.description}
                      </Typography>
                    </Box>
                    {nextTask.destination ? (
                      <Button
                        variant="contained"
                        color="inherit"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (nextTask.destination) {
                            navigate(nextTask.destination);
                          }
                        }}
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          ml: { sm: 'auto' },
                          bgcolor: 'common.white',
                          color: 'primary.main',
                          '&:hover': {
                            bgcolor: 'grey.100',
                          },
                        }}
                      >
                        Ouvrir
                      </Button>
                    ) : null}
                  </Stack>
                </Box>
              ) : (
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {currentEvent.isValidated
                      ? 'Tout est bon, on vous attend le jour J.'
                      : "Votre dossier est complet. L'orga doit maintenant valider votre participation."}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {todoTasks.length > 0 ? (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">Vos actions</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Les démarches que vous pouvez faire dès maintenant.
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  {todoTasks.map((task) => (
                    <DashboardTaskRow
                      key={task.id}
                      task={task}
                      onNavigate={navigate}
                    />
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {blockedTasks.length > 0 ? (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">Bloqué</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {
                      "Ces étapes dépendent d'une action précédente de votre part."
                    }
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  {blockedTasks.map((task) => (
                    <DashboardTaskRow
                      key={task.id}
                      task={task}
                      onNavigate={navigate}
                    />
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {pendingTasks.length > 0 ? (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">En attente</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {
                      "Ces étapes dépendent d'une action extérieure, par exemple de l'orga."
                    }
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  {pendingTasks.map((task) => (
                    <DashboardTaskRow
                      key={task.id}
                      task={task}
                      onNavigate={navigate}
                    />
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {doneTasks.length > 0 ? (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">Déjà terminé</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Un récapitulatif rapide de ce qui est déjà validé
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  {doneTasks.map((task) => (
                    <DashboardTaskRow
                      key={task.id}
                      task={task}
                      onNavigate={navigate}
                    />
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : null}
      </Stack>
    </Layout>
  );
};
